import {
  useEffect,
  useState,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  AD, AR, AT, AU, AW, BA, BE, BO, BR, CA, CD, CH, CI, CL, CO, CR, CV, CW, CZ,
  DE, DK, DO, DZ, EC, EG, ES, FI, FR, GH, GT, HN, HR, HT, HU, IE, IQ, IR, IS,
  JO, JP, KR, MA, MK, MX, NG, NI, NL, NO, NZ, PA, PE, PL, PT, PY, QA, RS, RU,
  SA, SE, SI, SN, SV, TN, TR, US, UY, UZ, VE, ZA,
} from 'country-flag-icons/react/3x2';
import { haptics } from '@/lib/native';
import type { CountryCode, FlagPalette, Friend, Variant } from '@/types/domain';

// Decorative avatar stack for social-proof rows. The product no longer has a
// real friend graph (no IG integration, no contacts) — these are stylized
// placeholder avatars used purely for visual rhythm. When the social feature
// is reconsidered, this whole list goes away.
const PLACEHOLDER_FACES: Friend[] = [
  { name: 'Sofia', tint: '#E8C2A8' },
  { name: 'Marco', tint: '#B8C9DD' },
  { name: 'Jules', tint: '#D9C3D9' },
  { name: 'Kenji', tint: '#C2D9C0' },
  { name: 'Amara', tint: '#E0C8B0' },
  { name: 'Ravi', tint: '#C8B8DC' },
  { name: 'Lola', tint: '#DCC8B0' },
  { name: 'Theo', tint: '#B8D2D8' },
  { name: 'Nina', tint: '#E0BCB8' },
];

type FlagComponent = ComponentType<{ title?: string; style?: CSSProperties }>;

// England (St. George's Cross) — country-flag-icons only ships ISO 3166-1
// alpha-2 country flags, not sub-national flags, so we hand-roll this one.
const EnglandFlag: FlagComponent = ({ title, style }) => (
  <svg
    viewBox="0 0 60 36"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
    aria-label={title}
    preserveAspectRatio="none"
  >
    <rect width="60" height="36" fill="#FFFFFF" />
    <rect x="24" y="0" width="12" height="36" fill="#CF142B" />
    <rect x="0" y="12" width="60" height="12" fill="#CF142B" />
  </svg>
);

// Scotland (St. Andrew's Cross / Saltire) — also sub-national, also missing
// from country-flag-icons. Blue field, white diagonal cross.
const ScotlandFlag: FlagComponent = ({ title, style }) => (
  <svg
    viewBox="0 0 60 36"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
    aria-label={title}
    preserveAspectRatio="none"
  >
    <rect width="60" height="36" fill="#005EB8" />
    <path d="M0 0 L60 36 M60 0 L0 36" stroke="#FFFFFF" strokeWidth="6" />
  </svg>
);

// Wales — sub-national, not in country-flag-icons. White-over-green field;
// the dragon is omitted because it doesn't render legibly at chip sizes.
const WalesFlag: FlagComponent = ({ title, style }) => (
  <svg
    viewBox="0 0 60 36"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
    aria-label={title}
    preserveAspectRatio="none"
  >
    <rect width="60" height="18" fill="#FFFFFF" />
    <rect y="18" width="60" height="18" fill="#00683E" />
  </svg>
);

// Northern Ireland — FIFA-recognized football nation but no agreed civil
// flag. Football contexts use the Ulster Banner: white field, red cross,
// central white star with red Hand of Ulster. Star/hand omitted at chip
// scale; the cross alone is the recognizable element.
const NorthernIrelandFlag: FlagComponent = ({ title, style }) => (
  <svg
    viewBox="0 0 60 36"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
    aria-label={title}
    preserveAspectRatio="none"
  >
    <rect width="60" height="36" fill="#FFFFFF" />
    <rect x="24" y="0" width="12" height="36" fill="#CF142B" />
    <rect x="0" y="12" width="60" height="12" fill="#CF142B" />
  </svg>
);

// Tree-shaken: only the countries we ship are bundled.
const FLAG_COMPONENTS: Record<CountryCode, FlagComponent> = {
  // Curated 18
  USA: US,
  BRA: BR,
  FRA: FR,
  ARG: AR,
  POR: PT,
  ECU: EC,
  MEX: MX,
  COL: CO,
  KOR: KR,
  CRO: HR,
  POL: PL,
  JPN: JP,
  ENG: EnglandFlag,
  SEN: SN,
  GER: DE,
  MAR: MA,
  ALG: DZ,
  COD: CD,
  // Schedule-only WC participants
  AUS: AU,
  AUT: AT,
  BEL: BE,
  BIH: BA,
  CAN: CA,
  CIV: CI,
  CPV: CV,
  CUW: CW,
  CZE: CZ,
  EGY: EG,
  ESP: ES,
  GHA: GH,
  HAI: HT,
  IRN: IR,
  IRQ: IQ,
  JOR: JO,
  KSA: SA,
  NED: NL,
  NOR: NO,
  NZL: NZ,
  PAN: PA,
  PAR: PY,
  QAT: QA,
  RSA: ZA,
  SCO: ScotlandFlag,
  SUI: CH,
  SWE: SE,
  TUN: TN,
  TUR: TR,
  URU: UY,
  UZB: UZ,
  // Additional WC qualifiers / seeded countries
  HUN: HU,
  RUS: RU,
  IRL: IE,
  NCA: NI,
  MKD: MK,
  AND: AD,
  CRC: CR,
  ISL: IS,
  SRB: RS,
  FIN: FI,
  WAL: WalesFlag,
  DEN: DK,
  DOM: DO,
  GUA: GT,
  ARU: AW,
  SLV: SV,
  BOL: BO,
  CHI: CL,
  VEN: VE,
  HON: HN,
  SVN: SI,
  NIR: NorthernIrelandFlag,
  PER: PE,
  NGA: NG,
};

interface FlagProps {
  code: CountryCode;
  w?: number;
  h?: number;
  radius?: number;
  style?: CSSProperties | undefined;
}

const warnedMissingFlags = new Set<string>();

export function Flag({ code, w = 22, h = 16, radius = 3, style }: FlagProps) {
  const Component = FLAG_COMPONENTS[code];
  const wrapper: CSSProperties = {
    width: w,
    height: h,
    borderRadius: radius,
    overflow: 'hidden',
    display: 'inline-block',
    flexShrink: 0,
    boxShadow: '0 0 0 0.5px rgba(0,0,0,0.15)',
    background: '#e5e1d8',
    ...style,
  };
  if (!Component) {
    if (!warnedMissingFlags.has(code)) {
      warnedMissingFlags.add(code);
      console.warn(
        `[Flag] No flag component registered for code "${code}". ` +
          `Add it to FLAG_COMPONENTS in components/primitives.tsx (and to CountryCode in types/domain.ts if missing).`,
      );
    }
    return (
      <span
        style={{
          ...wrapper,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#d6cfc4',
          color: '#5a544c',
          fontFamily: 'ui-monospace, SF Mono, monospace',
          fontSize: Math.max(7, Math.floor(h * 0.5)),
          fontWeight: 700,
          letterSpacing: 0.3,
        }}
        aria-label={code}
        title={code}
      >
        {code}
      </span>
    );
  }
  return (
    <span style={wrapper} aria-label={code}>
      <Component title={code} style={{ width: '100%', height: '100%', display: 'block' }} />
    </span>
  );
}

interface FlagStripeProps {
  colors: FlagPalette;
  /**
   * Optional: when supplied, renders the real flag SVG instead of the
   * abstract tri-stripe. Lets us migrate call sites incrementally without
   * changing every prop list.
   */
  code?: CountryCode;
  w?: number;
  h?: number;
  radius?: number;
  style?: CSSProperties;
}

export function FlagStripe({ colors, code, w = 22, h = 16, radius = 3, style }: FlagStripeProps) {
  if (code) return <Flag code={code} w={w} h={h} radius={radius} style={style} />;
  return (
    <div
      style={{
        width: w,
        height: h,
        borderRadius: radius,
        overflow: 'hidden',
        display: 'flex',
        flexShrink: 0,
        boxShadow: '0 0 0 0.5px rgba(0,0,0,0.15)',
        ...style,
      }}
    >
      <div style={{ flex: 1, background: colors.primary }} />
      <div style={{ flex: 1, background: colors.secondary }} />
      <div style={{ flex: 1, background: colors.tertiary }} />
    </div>
  );
}

interface AvatarProps {
  name: string;
  size?: number;
  tint?: string;
  ring?: string;
}

export function Avatar({ name, size = 28, tint = '#ddd', ring = '#fff' }: AvatarProps) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size,
        background: tint,
        color: '#3a2f24',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, system-ui',
        fontWeight: 600,
        fontSize: size * 0.4,
        boxShadow: `0 0 0 2px ${ring}`,
        flexShrink: 0,
      }}
    >
      {name[0]}
    </div>
  );
}

interface FriendStackProps {
  count?: number;
  size?: number;
  ring?: string;
  friends?: Friend[];
}

export function FriendStack({
  count = 3,
  size = 24,
  ring = '#fff',
  friends = PLACEHOLDER_FACES,
}: FriendStackProps) {
  const visible = friends.slice(0, count);
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={{ display: 'flex' }}>
        {visible.map((f, i) => (
          <div key={f.name} style={{ marginLeft: i === 0 ? 0 : -size * 0.35 }}>
            <Avatar name={f.name} size={size} tint={f.tint} ring={ring} />
          </div>
        ))}
      </div>
    </div>
  );
}

interface PhotoPlaceholderProps {
  /** A real photo — venue-supplied or licensed. When present it's rendered
   * and the generated fallback is skipped. */
  imageUrl?: string | null;
  /** Name the monogram is derived from (venue or event title). Omit for a
   * plain textured block. */
  label?: string;
  height?: number;
  palette?: FlagPalette;
  style?: CSSProperties;
}

// Leading articles are dropped so "The Crown" reads as "C", not "T".
const MONOGRAM_SKIP = new Set(['the', 'el', 'la', 'le', 'les', 'los', 'a']);

function monogramFor(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  const meaningful = words.filter(
    (w, i) => !(i === 0 && words.length > 1 && MONOGRAM_SKIP.has(w.toLowerCase())),
  );
  const use = meaningful.length > 0 ? meaningful : words;
  const first = use[0];
  if (!first) return '';
  const second = use[1];
  if (!second) return first.slice(0, 2).toUpperCase();
  return `${first[0] ?? ''}${second[0] ?? ''}`.toUpperCase();
}

/**
 * Stand-in for a venue/event image. Renders `imageUrl` when one exists;
 * otherwise a flag-palette textured block with the label's monogram — an
 * honestly-generated placeholder, never a stock photo posing as the real
 * place.
 */
export function PhotoPlaceholder({
  imageUrl,
  label,
  height = 140,
  palette,
  style,
}: PhotoPlaceholderProps) {
  if (imageUrl) {
    return (
      <div style={{ height, width: '100%', overflow: 'hidden', ...style }}>
        <img
          src={imageUrl}
          alt={label ?? ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }
  const c1 = palette?.primary ?? '#888';
  const c2 = palette?.secondary ?? '#aaa';
  const c3 = palette?.tertiary ?? '#ccc';
  const bg = `repeating-linear-gradient(135deg, ${c1}22 0 8px, ${c2}22 8px 16px, ${c3}22 16px 24px)`;
  const monogram = label ? monogramFor(label) : '';
  return (
    <div
      style={{
        height,
        width: '100%',
        position: 'relative',
        background: `${bg}, #f4f2ee`,
        backgroundBlendMode: 'multiply',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      {monogram ? (
        <span
          style={{
            fontFamily: '-apple-system, system-ui, sans-serif',
            fontWeight: 800,
            fontSize: Math.max(15, Math.round(height * 0.4)),
            letterSpacing: 1,
            color: 'rgba(26,22,18,0.38)',
            userSelect: 'none',
          }}
        >
          {monogram}
        </span>
      ) : null}
    </div>
  );
}

interface GoingPillProps {
  going: boolean;
  onToggle: () => void;
  palette: FlagPalette;
  variant?: Variant;
}

export function GoingPill({ going, onToggle, palette, variant = 'sporty' }: GoingPillProps) {
  const { t } = useTranslation();
  if (variant === 'editorial') {
    return (
      <button
        type="button"
        onClick={onToggle}
        style={{
          border: `1px solid ${going ? palette.primary : '#2a2520'}`,
          background: going ? palette.primary : 'transparent',
          color: going ? '#fff' : '#2a2520',
          padding: '8px 16px',
          borderRadius: 0,
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontSize: 14,
          letterSpacing: 0.5,
          cursor: 'pointer',
          textTransform: 'uppercase',
        }}
      >
        {going ? t('goingCheckTrailing') : t('imGoing')}
      </button>
    );
  }
  if (variant === 'minimal') {
    return (
      <button
        type="button"
        onClick={onToggle}
        style={{
          border: '1px solid rgba(0,0,0,0.12)',
          background: going ? '#0a0a0a' : '#fff',
          color: going ? '#fff' : '#0a0a0a',
          padding: '7px 14px',
          borderRadius: 999,
          fontFamily: '-apple-system, system-ui',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        {going ? t('going') : t('imGoing')}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        border: 'none',
        background: going ? palette.primary : '#0a0a0a',
        color: '#fff',
        padding: '9px 18px',
        borderRadius: 999,
        fontFamily: '-apple-system, system-ui',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        boxShadow: going ? `0 4px 14px ${palette.primary}66` : 'none',
        transition: 'all .2s',
      }}
    >
      {going ? t('goingCheck') : t('imGoing')}
    </button>
  );
}

interface SaveToggleProps {
  saved: boolean;
  onToggle: () => void;
  /** Used in the accessible name: "Save Favela Grill" / "Saved Favela Grill". */
  venueName: string;
  size?: number;
}

// Length of the heart-pop animation, in ms. Used to auto-clear the
// `popping` state so the animation prop unsets cleanly between saves.
const HEART_POP_MS = 320;

export function SaveToggle({ saved, onToggle, venueName, size = 22 }: SaveToggleProps) {
  const { t } = useTranslation();
  // Counter that bumps on every save (not unsave). Used as a key on the
  // SVG so the CSS pop animation retriggers cleanly each time. Resets to
  // 0 after the animation completes so the SVG returns to a stable
  // animation-free state between saves.
  const [popKey, setPopKey] = useState(0);
  useEffect(() => {
    if (popKey === 0) return;
    const t = setTimeout(() => setPopKey(0), HEART_POP_MS + 50);
    return () => clearTimeout(t);
  }, [popKey]);
  return (
    <button
      type="button"
      onClick={(e) => {
        // The heart often sits inside row-level tappable surfaces; don't
        // let a save propagate up into a row click.
        e.stopPropagation();
        // Save is a noticeable commitment; unsave is a quieter undo. The
        // stronger save haptic mirrors the heart-pop animation that only
        // fires on save — sits below the success haptic the "I'm going"
        // flow uses, preserving the action hierarchy.
        if (saved) {
          haptics.light();
        } else {
          haptics.medium();
          setPopKey((k) => k + 1);
        }
        onToggle();
      }}
      aria-label={saved ? t('saveToggle.saved', { venue: venueName }) : t('saveToggle.save', { venue: venueName })}
      aria-pressed={saved}
      style={{
        width: size + 14,
        height: size + 14,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        flexShrink: 0,
      }}
    >
      <svg
        // `key` retriggers the pop animation on every fresh save by
        // remounting the SVG. Microscopic remount cost.
        key={popKey}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={saved ? '#d1393a' : 'none'}
        stroke={saved ? '#d1393a' : '#9a9590'}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          transition: 'fill .15s, stroke .15s',
          transformOrigin: 'center',
          animation:
            popKey > 0
              ? `nynjwc-heart-pop ${HEART_POP_MS}ms cubic-bezier(.34,1.56,.64,1)`
              : undefined,
        }}
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    </button>
  );
}

export function VariantHeading({
  children,
  variant = 'sporty',
}: {
  children: ReactNode;
  variant?: Variant;
}) {
  if (variant === 'editorial') {
    return (
      <div
        style={{
          fontFamily: '"Instrument Serif", "Cormorant Garamond", Georgia, serif',
          fontSize: 24,
          fontWeight: 400,
          color: '#1a1612',
          letterSpacing: -0.4,
          lineHeight: 1.1,
          marginBottom: 12,
        }}
      >
        {children}
      </div>
    );
  }
  if (variant === 'minimal') {
    return (
      <div
        style={{
          fontFamily: '-apple-system, system-ui',
          fontSize: 13,
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: 1.2,
          color: '#888',
          marginBottom: 10,
        }}
      >
        {children}
      </div>
    );
  }
  return (
    <div
      style={{
        fontFamily: '-apple-system, system-ui',
        fontSize: 18,
        fontWeight: 800,
        color: '#0a0a0a',
        letterSpacing: -0.3,
        marginBottom: 12,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </div>
  );
}
